import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import { ElementType, FC, forwardRef, ReactElement, ReactNode, Ref } from 'react';

import MuiAutocomplete, {
  AutocompleteProps as MuiAutocompleteProps,
  autocompleteClasses,
  AutocompletePropsSizeOverrides,
} from '@mui/material/Autocomplete';

import { ChipTypeMap } from '@mui/material/Chip';
import Popper from '@mui/material/Popper';
import { styled } from '@mui/material/styles';

import { OverridableStringUnion } from '@mui/types';

import InputAdornment from '../InputAdornment';
import { InputBaseProps } from '../InputBase';
import TextField, { TextFieldProps } from '../TextField';

import Palette from '../theme/palette';

import AutocompleteListBoxComponent from './AutocompleteListBoxComponent';

declare module '@mui/material/Autocomplete' {
  interface AutocompletePropsSizeOverrides {
    large: true;
  }
}

export interface AutocompleteProps<
  T,
  Multiple extends boolean = false,
  DisableClearable extends boolean = false,
  FreeSolo extends boolean = false,
  ChipComponent extends ElementType = ChipTypeMap['defaultComponent']
> extends Omit<MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo, ChipComponent>, 'renderInput'> {
  renderInput?: (params: TextFieldProps) => ReactNode;
  ListBoxComponent?: MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo, ChipComponent>['ListboxComponent'];
  PopperComponent?: MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo, ChipComponent>['PopperComponent'];
  disableListWrap?: MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo, ChipComponent>['disableListWrap'];
  defaultValue?: MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo, ChipComponent>['defaultValue'];

  /**
   * Value of the input.
   */
  value?: string | any;
  label?: string;

  /**
   * Color of the input.
   */
  color?: 'primary' | 'secondary' | 'internal' | 'success' | 'error' | 'warning';

  /**
   * Same as Material UI variant. We use "outlined" only for now. This can change in the future, though.
   */
  variant?: 'outlined' | 'filled' | 'standard';

  minWidth?: string;
  error?: boolean;

  /**
   * Function to extract the displayed text from an option.
   * BEWARE: if using a custom option type and it doesn't have a `label` property, you MUST supply this function so
   * that your options do not show up as empty strings!
   */
  getOptionLabel?: MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>['getOptionLabel'];

  /**
   * Virtualized autocomplete list (for > 10 items in list)
   */
  virtualized?: boolean;

  /**
   * Optional content for the start adornment of the text input field.
   * Unlike in MUI, wrapping this content in `<InputAdornment />` is handled internally, so DO NOT do it manually!
   * If a custom `renderInput` method is provided, the adornment is passed as part of its `params` argument.
   */
  startAdornment?: ReactNode;

  /**
   * Optional content for the end adornment of the text input field.
   * Unlike in MUI, wrapping this content in `<InputAdornment />` is handled internally, so DO NOT do it manually!
   * If a custom `renderInput` method is provided, the adornment is passed as part of its `params` argument.
   */
  endAdornment?: ReactNode;

  size?: OverridableStringUnion<NonNullable<InputBaseProps['size']>, AutocompletePropsSizeOverrides>;
}

export interface AutocompleteOption {
  label: string;
}

export interface AutocompleteOptionWithValue extends AutocompleteOption {
  value: number | string;
}

const StyledMuiAutocomplete = styled(MuiAutocomplete, {
  shouldForwardProp: (prop) => prop !== 'minWidth' && prop !== 'virtualized',
})<AutocompleteProps<string | { label: string } | { label: string; value: number | string }>>(({ minWidth }) => ({
  minWidth,
})) as FC<AutocompleteProps<any>>;

const O2Autocomplete = <T,>(props: AutocompleteProps<T>) => {
  /**
   * Default version used when the `getOptionLabel` prop is not provided.
   */
  const defaultOptionLabel = (option: T | AutocompleteOption | AutocompleteOptionWithValue) => {
    if (typeof option === 'string') {
      return option;
    }

    if ('label' in option) {
      return option.label;
    }

    throw Error('Values must be array of string or object with label prop.');
  };

  /**
   * Default version used when the `renderInput` prop is not provided.
   */
  const defaultRenderInput = (params: TextFieldProps) => {
    return <TextField {...params} label={label} color={color} variant={variant} error={error} />;
  };

  const {
    label,
    color,
    variant,
    error = false,
    virtualized = false,
    getOptionLabel = defaultOptionLabel,
    minWidth,
    noOptionsText = 'o2-ui.noOptionsText',
    loadingText = 'o2-ui.loadingText',
    startAdornment,
    endAdornment,
    renderInput = defaultRenderInput,
    ...otherProps
  } = props;

  /**
   * Add adornment and size props to the text field render function.
   *
   * Adding size manually is required because MUI force-sets fullWidth and only supports 'small', not 'large' like we do:
   * https://github.com/mui/material-ui/blob/v5.10.7/packages/mui-material/src/Autocomplete/Autocomplete.js#L545
   */
  const renderInputWithAdornmentsAndSize = (params: TextFieldProps) => {
    return renderInput({
      ...params,
      fullWidth: props.fullWidth,
      size: props.size,

      InputProps: {
        ...params.InputProps,
        startAdornment: startAdornment ? (
          <>
            <InputAdornment position="start">{startAdornment}</InputAdornment>
            {params.InputProps?.startAdornment}
          </>
        ) : (
          params.InputProps?.startAdornment
        ),
        endAdornment: endAdornment ? (
          <>
            <InputAdornment position="end">{endAdornment}</InputAdornment>
            {params.InputProps?.endAdornment}
          </>
        ) : (
          params.InputProps?.endAdornment
        ),
      },
    });
  };

  const getOptionValue = (option: T | AutocompleteOption | AutocompleteOptionWithValue, label: string) => {
    if (option !== null && typeof option === 'object' && 'value' in option) {
      return option.value;
    }

    return label;
  };

  const renderOption: AutocompleteProps<T>['renderOption'] = (props, option, { inputValue }) => {
    const label = getOptionLabel(option);
    const matches = match(label, inputValue, {
      insideWords: true,
    });
    const parts = parse(label, matches);

    const content = (
      <div>
        {parts.map((part, index) => (
          <span
            key={index}
            style={{
              fontWeight: part.highlight ? 700 : 400,
              color: color && part.highlight ? Palette[color].main : undefined,
            }}
          >
            {part.text}
          </span>
        ))}
      </div>
    );

    if (virtualized) {
      return [props, content];
    }

    return (
      <li data-value={getOptionValue(option, label)} {...props}>
        {content}
      </li>
    );
  };

  const StyledPopper = styled(Popper)({
    [`& .${autocompleteClasses.listbox}`]: {
      boxSizing: 'border-box',
      '& ul': {
        padding: 0,
        margin: 0,
      },
    },
  });

  return (
    <StyledMuiAutocomplete
      componentsProps={{
        paper: {
          sx: {
            border: color === 'internal' ? `2px solid ${Palette.internal.main}` : 'none',
            borderRadius: color === 'internal' ? '8px' : 'none',
            boxShadow:
              '0px 5px 5px -3px rgb(0 0 0 / 20%), 0px 8px 10px 1px rgb(0 0 0 / 14%), 0px 3px 14px 2px rgb(0 0 0 / 12%)',
          },
        },
      }}
      ListboxProps={
        !virtualized
          ? {
              style: {
                padding: 0,
              },
            }
          : undefined
      }
      disableListWrap={virtualized}
      PopperComponent={virtualized ? StyledPopper : undefined}
      ListboxComponent={virtualized ? AutocompleteListBoxComponent : undefined}
      minWidth={minWidth}
      renderInput={renderInputWithAdornmentsAndSize}
      renderOption={renderOption}
      getOptionLabel={getOptionLabel}
      noOptionsText={noOptionsText}
      loadingText={loadingText}
      {...otherProps}
    />
  );
};

type AutocompleteType = <
  T,
  Multiple extends boolean = false,
  DisableClearable extends boolean = false,
  FreeSolo extends boolean = false
>(
  props: AutocompleteProps<T, Multiple, DisableClearable, FreeSolo> & {
    ref?: Ref<HTMLDivElement>;
  }
) => ReactElement;

//@ts-expect-error Types of property 'getOptionLabel' are incompatible - can't be fixed straightforwardly
const Autocomplete = forwardRef((props, ref) => <O2Autocomplete ref={ref} {...props} />) as AutocompleteType;
//@ts-expect-error TS2339: Property 'displayName' does not exist on type 'AutocompleteType'.
Autocomplete.displayName = 'Autocomplete';

export default Autocomplete;
